import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * WiFi-preparation checklist (onboarding).
 *
 * Before Huri dispatches a technician, the customer prepares the site's
 * network. This card spells out — in plain, non-technical language —
 * what needs to be ready, and lets a manager confirm "it's done". The
 * confirmation is a readiness signal Huri can see on the admin side.
 *
 * Phase 5.G PR-9: once the customer has marked the network as ready,
 * the green confirmation banner used to sit on the page forever — taking
 * a 60px slice off the top of every visit even months after the
 * technician was already on site. Now it's a transient toast that shows
 * for 30 seconds RIGHT AFTER the user clicks "סימנתי", then hides
 * itself. On any subsequent page load (data already includes a
 * ``wifi_prep_done_at``) the card renders nothing at all — the
 * readiness state still lives in the database (admins still see it),
 * but the customer's screen is back to the gates dashboard.
 *
 * Props:
 *  - siteId
 *  - initialDoneAt: ISO string or null (sites.wifi_prep_done_at)
 *  - canManage: boolean — only a manager may toggle
 *  - onChange(doneAt): notified after a successful toggle
 */
const CHECKLIST = [
  'רשת WiFi נפרדת לשערים (רצוי VLAN נפרד מהרשת המשרדית)',
  'שם הרשת (SSID) והסיסמה מוכנים ורשומים',
  'יש קליטת WiFi טובה ליד כל שער שבמפה',
  'הרשת מחוברת לאינטרנט ופעילה',
];

const CONFIRMATION_VISIBLE_MS = 30000;

function WifiPrepCard({ siteId, initialDoneAt, canManage, onChange }) {
  const [doneAt, setDoneAt] = useState(initialDoneAt || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Whether the green confirmation banner is currently visible. True
  // only for ~30s after the user clicks "סימנתי" in this session.
  // Stays false on page reloads / mount-with-existing-doneAt so the
  // banner doesn't permanently squat at the top of the page.
  const [showConfirmation, setShowConfirmation] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  const setDone = async (done) => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(`/api/customer/sites/${siteId}/wifi-prep`, { done });
      setDoneAt(res.data.wifi_prep_done_at);
      if (onChange) onChange(res.data.wifi_prep_done_at);

      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      if (done) {
        // Just marked complete — show the green confirmation, then
        // hide it after 30s so the dashboard reclaims the space.
        setShowConfirmation(true);
        hideTimer.current = setTimeout(() => {
          setShowConfirmation(false);
          hideTimer.current = null;
        }, CONFIRMATION_VISIBLE_MS);
      } else {
        // User clicked "בטל סימון" — go back to the checklist.
        setShowConfirmation(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'העדכון נכשל');
    } finally {
      setSaving(false);
    }
  };

  // Already marked done before this session — render nothing. The
  // dashboard gets the full screen height; admins still see the
  // readiness flag via the API.
  if (doneAt && !showConfirmation) {
    return null;
  }

  if (showConfirmation && doneAt) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
        background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46',
        borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1.25rem',
        fontSize: '0.88rem',
      }}>
        <span>✓ הכנות הרשת סומנו כהושלמו · {new Date(doneAt).toLocaleDateString('he-IL')}</span>
        <div style={{ flex: 1 }} />
        {canManage && (
          <button
            onClick={() => setDone(false)}
            disabled={saving}
            style={{
              border: 'none', background: 'transparent', color: '#047857',
              fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            בטל סימון
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
      padding: '0.9rem 1rem', marginBottom: '1.25rem',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.3rem' }}>
        הכנות רשת לפני הגעת הטכנאי
      </div>
      <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        כדי שההתקנה תעבור חלק, ודאו שהדברים הבאים מוכנים באתר לפני שאנחנו שולחים טכנאי:
      </div>
      <ul style={{ margin: 0, paddingInlineStart: '1.2rem', fontSize: '0.86rem', color: '#374151', lineHeight: 1.8 }}>
        {CHECKLIST.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
      {error && <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginTop: '0.6rem' }}>{error}</div>}
      {canManage && (
        <button
          onClick={() => setDone(true)}
          disabled={saving}
          style={{
            marginTop: '0.8rem', padding: '0.45rem 0.95rem', border: 'none',
            background: '#1f2937', color: '#fff', borderRadius: 6,
            fontSize: '0.85rem', cursor: 'pointer',
          }}
        >
          {saving ? 'שומר...' : 'סימנתי — הרשת מוכנה'}
        </button>
      )}
    </div>
  );
}

export default WifiPrepCard;
